import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../api/axiosInstance";
import { formatDateForDisplay } from "../utils/formatters";
import { CUSTODIAN_OPTIONS, DESTINATION_FIRM_OPTIONS, SOURCE_CUSTODIAN_OPTIONS } from "../constants/projectConstants";
import { useToast } from "../context/ToastContext";
import { humanizeError } from "../utils/userFriendlyError";

const getDefaultTargetEndDate = () => {
  const now = new Date();
  now.setDate(now.getDate() + 42);
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const AUM_AVAILABILITY_OPTIONS = {
  1: "Yes — I'll enter values during household setup",
  2: "I'll add it via document import",
  3: "No — Skip AUM tracking for this project"
};

function Dashboard({ user }) {
  const { toast } = useToast();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProjectId, setLoadingProjectId] = useState(null);
  const [finalizingProject, setFinalizingProject] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [step, setStep] = useState(1);
  const [projectTitle, setProjectTitle] = useState("");
  const [custodian, setCustodian] = useState("Fidelity");
  const [orgId, setOrgId] = useState(null);
  const [editingProjectId, setEditingProjectId] = useState(null);
  // New State for Refactored Flow
  const [destinationFirm, setDestinationFirm] = useState("");
  const [sourceCustodian, setSourceCustodian] = useState("");
  const [custodianTypes, setCustodianTypes] = useState({ iws: true, nfs: true });
  const [feeScheduleType, setFeeScheduleType] = useState("Standard");
  const [customFeePercentage, setCustomFeePercentage] = useState("");
  const [targetEndDate, setTargetEndDate] = useState(getDefaultTargetEndDate);
  const [aumAvailability, setAumAvailability] = useState(1);

  // Advisors
  const [advisors, setAdvisors] = useState([{ first_name: "", last_name: "", g_number: "", rr_code: "", email: "" }]);

  // IWS Default Details
  const [iwsDefaultAdvisorIndex, setIwsDefaultAdvisorIndex] = useState("");
  const [iwsAdditionalAuthorisedOwner, setIwsAdditionalAuthorisedOwner] = useState({ single_name: "", g_number: "" });
  const [iwsAdvisorFirmName, setIwsAdvisorFirmName] = useState({ single_name: "", g_number: "" });
  
  // IWS Preferences
  const [iwsProxies, setIwsProxies] = useState({ send_to_advisor: true, send_to_client: false });
  const [iwsProxyGNumberEnabled, setIwsProxyGNumberEnabled] = useState(false);
  const [iwsReports, setIwsReports] = useState({ send_to_advisor: true, send_to_client: false });
  const [iwsProspectuses, setIwsProspectuses] = useState({ send_to_advisor: true, send_to_client: false });
  const [iwsCorporateAction, setIwsCorporateAction] = useState({ send_to_advisor: true, send_to_client: false });
  const [iwsOutsourcingAgentAuthorisation, setIwsOutsourcingAgentAuthorisation] = useState(true);
  
  // NFS Default Details - Updated: Removed RR1 Rep of Record and constant flag for Branch Prefix
  const [nfsBranchPrefix, setNfsBranchPrefix] = useState(""); 

  const [nfsAreHoldersEmployees, setNfsAreHoldersEmployees] = useState("No");
  const [nfsIsManagedAccount, setNfsIsManagedAccount] = useState("No");
  
  // NFS Preferences
  const [nfsConsultWithBroker, setNfsConsultWithBroker] = useState({ yes: true, no: false });
  const [nfsMakeOwnDecision, setNfsMakeOwnDecision] = useState({ yes: true, no: false });
  const [nfsConsultWithFamilyFriends, setNfsConsultWithFamilyFriends] = useState({ yes: false, no: true });
  const [nfsEdelivery, setNfsEdelivery] = useState(true);

  const targetEndDateRef = useRef(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    // Get org_id from user data
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    if (userData.user?.org_id) {
      setOrgId(userData.user.org_id);
    } else if (user?.org_id) {
      setOrgId(user.org_id);
    } else if (userData.organization?.id) {
      setOrgId(userData.organization.id);
    }
    fetchProjects();
  }, [user]);

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get("/projects");
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setProjectTitle("");
    setCustodian("Fidelity");
    setDestinationFirm("");
    setSourceCustodian("");
    setCustodianTypes({ iws: true, nfs: true });
    setFeeScheduleType("Standard");
    setCustomFeePercentage("");
    setTargetEndDate(getDefaultTargetEndDate());
    setAumAvailability(1);
    setAdvisors([{ first_name: "", last_name: "", g_number: "", rr_code: "", email: "" }]);
    setEditingProjectId(null);
    
    // Reset defaults
    setIwsDefaultAdvisorIndex("");
    setIwsAdditionalAuthorisedOwner({ single_name: "", g_number: "" });
    setIwsAdvisorFirmName({ single_name: "", g_number: "" });
    setIwsProxies({ send_to_advisor: true, send_to_client: false });
    setIwsProxyGNumberEnabled(false);
    setIwsReports({ send_to_advisor: true, send_to_client: false });
    setIwsProspectuses({ send_to_advisor: true, send_to_client: false });
    setIwsCorporateAction({ send_to_advisor: true, send_to_client: false });
    setIwsOutsourcingAgentAuthorisation(true);
    setNfsBranchPrefix("");

    setNfsAreHoldersEmployees("No");
    setNfsIsManagedAccount("No");
    setNfsConsultWithBroker({ yes: true, no: false });
    setNfsMakeOwnDecision({ yes: true, no: false });
    setNfsConsultWithFamilyFriends({ yes: false, no: true });
    setNfsEdelivery(true);
  };

  // Advisor Helpers
  const handleAddAdvisor = () => {
    setAdvisors([...advisors, { first_name: "", last_name: "", g_number: "", rr_code: "", email: "" }]);
  };

  const handleRemoveAdvisor = (index) => {
    setAdvisors(advisors.filter((_, i) => i !== index));
  };

  const handleAdvisorChange = (index, field, value) => {
    const updated = [...advisors];
    updated[index][field] = value;
    setAdvisors(updated);
  };

  const validateStep = () => {
    if (step === 1) {
      if (!projectTitle.trim()) {
        toast.error("Project title is required.");
        return false;
      }
      if (!destinationFirm) {
        toast.error("Pick a destination firm.");
        return false;
      }
      if (!targetEndDate) {
        toast.error("Pick a target end date.");
        return false;
      }
    }
    if (step === 2) {
      const validAdvisors = advisors.filter(a =>
        a.first_name && a.last_name && (a.g_number || a.rr_code) && a.email
      );
      if (validAdvisors.length === 0 && advisors.length > 0) {
        const missingEmail = advisors.some(a => !a.email);
        const missingIds = advisors.some(a => !a.g_number && !a.rr_code);

        if (missingEmail) {
          toast.error("Advisor email is required.");
          return false;
        }
        if (missingIds) {
          toast.error("Each advisor needs a G Number or RR Code.");
          return false;
        }
      }
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const handlePrevStep = () => {
    setStep(step - 1);
  };

  // Exit Prompt Logic
  const [showExitPrompt, setShowExitPrompt] = useState(false);

  const handleCloseAttempt = () => {
      setShowExitPrompt(true);
  };

  const handleExitWithoutSaving = () => {
      setShowExitPrompt(false);
      setShowCreateModal(false);
      resetForm();
  };

  const [currentStatus, setCurrentStatus] = useState("created"); // Track status for edits
  
  const handleEditProject = (project) => {
    setEditingProjectId(project.id);
    setCurrentStatus(project.status); // Store current status
    setProjectTitle(project.title);
    setCustodian(project.custodian || "Fidelity");
    if (project.advisors && project.advisors.length > 0) {
      setAdvisors(project.advisors);
    } else {
      setAdvisors([{ first_name: "", last_name: "", g_number: "", rr_code: "", email: "" }]);
    }

    // Load defaults if exist
    setIwsDefaultAdvisorIndex(project.iws_default_advisor_index || "");
    if (project.iws_additional_authorised_owner) setIwsAdditionalAuthorisedOwner(project.iws_additional_authorised_owner);
    if (project.iws_advisor_firm_name) setIwsAdvisorFirmName(project.iws_advisor_firm_name);
    if (project.iws_proxies) setIwsProxies(project.iws_proxies);
    setIwsProxyGNumberEnabled(!!project.iws_proxies?.g_number);
    if (project.iws_reports) setIwsReports(project.iws_reports);
    if (project.iws_prospectuses) setIwsProspectuses(project.iws_prospectuses);
    if (project.iws_corporate_action) setIwsCorporateAction(project.iws_corporate_action);
    if (project.iws_outsourcing_agent_authorisation !== undefined) setIwsOutsourcingAgentAuthorisation(project.iws_outsourcing_agent_authorisation);
    
    if (project.destination_firm) setDestinationFirm(project.destination_firm);
    if (project.source_custodian) setSourceCustodian(project.source_custodian);
    if (project.custodian_types) setCustodianTypes(project.custodian_types);
    if (project.fee_schedule_type) setFeeScheduleType(project.fee_schedule_type);
    if (project.custom_fee_percentage) setCustomFeePercentage(project.custom_fee_percentage);

    if (project.nfs_branch_prefix) setNfsBranchPrefix(typeof project.nfs_branch_prefix === 'object' ? project.nfs_branch_prefix.value : project.nfs_branch_prefix);

    if (project.nfs_are_holders_employees) setNfsAreHoldersEmployees(project.nfs_are_holders_employees);
    if (project.nfs_is_managed_account) setNfsIsManagedAccount(project.nfs_is_managed_account);
    if (project.nfs_consult_with_broker) setNfsConsultWithBroker(project.nfs_consult_with_broker);
    if (project.nfs_make_own_decision) setNfsMakeOwnDecision(project.nfs_make_own_decision);
    if (project.nfs_consult_with_family_friends) setNfsConsultWithFamilyFriends(project.nfs_consult_with_family_friends);
    if (project.nfs_edelivery !== undefined) setNfsEdelivery(project.nfs_edelivery);

    if (project.target_end_date) setTargetEndDate(project.target_end_date);
    if (project.aum_availability) setAumAvailability(project.aum_availability);

    setShowCreateModal(true); // Reuse the same modal
  };

  const lockCustodianConfig = !!editingProjectId;

  const assembleProjectData = () => {
    const validAdvisors = advisors.filter(a => a.first_name && a.last_name);
    const data = {
      org_id: orgId,
      title: projectTitle,
      custodian: custodian,
      destination_firm: destinationFirm,
      source_custodian: sourceCustodian || null,
      custodian_types: custodianTypes,
      fee_schedule_type: feeScheduleType,
      custom_fee_percentage: feeScheduleType === "Custom" ? customFeePercentage : null,
      advisors: validAdvisors.length > 0 ? validAdvisors : null,
      // Status is managed by backend API logic (created vs finalize endpoint)
      // IWS
      iws_default_advisor_index: iwsDefaultAdvisorIndex || null,
      iws_additional_authorised_owner: (iwsAdditionalAuthorisedOwner.single_name || iwsAdditionalAuthorisedOwner.g_number) ? iwsAdditionalAuthorisedOwner : null,
      iws_advisor_firm_name: (iwsAdvisorFirmName.single_name || iwsAdvisorFirmName.g_number) ? iwsAdvisorFirmName : null,
      iws_proxies: iwsProxies,
      iws_reports: iwsReports,
      iws_prospectuses: iwsProspectuses,
      iws_corporate_action: iwsCorporateAction,
      iws_outsourcing_agent_authorisation: iwsOutsourcingAgentAuthorisation,
      // NFS
      nfs_branch_prefix: nfsBranchPrefix || null,

      nfs_are_holders_employees: nfsAreHoldersEmployees,
      nfs_is_managed_account: nfsIsManagedAccount,
      nfs_consult_with_broker: nfsConsultWithBroker,
      nfs_make_own_decision: nfsMakeOwnDecision,
      nfs_consult_with_family_friends: nfsConsultWithFamilyFriends,
      nfs_edelivery: nfsEdelivery,
      target_end_date: targetEndDate,
      aum_availability: aumAvailability
    };
    return data;
  };

  const handleCreateProject = async (e) => {
    if (e) e.preventDefault();
    if (!orgId) {
      toast.error("Organization ID not found. Please log in again.");
      return;
    }

    try {
      const projectData = assembleProjectData();
      const response = await axiosInstance.post("/projects", projectData);
      setProjects([...projects, response.data]);
      setShowCreateModal(false);
      resetForm();
      navigate(`/project/${response.data.id}`);
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error(humanizeError(error));
    }
  };

  const handleUpdateProject = async () => {
      if (!editingProjectId) return;
      try {
          const projectData = assembleProjectData();
          const response = await axiosInstance.put(`/projects/${editingProjectId}`, projectData);
          setProjects(projects.map(p => p.id === editingProjectId ? response.data : p));
          setShowCreateModal(false);
          resetForm();
          toast.success("Project updated.");
      } catch (error) {
          console.error("Error updating project:", error);
          toast.error(humanizeError(error));
      }
  };

  const handleFinalizeProject = async (e) => {
      if (e) e.preventDefault();
      if (!orgId) {
          toast.error("Organization ID not found. Please log in again.");
          return;
      }

      setFinalizingProject(true);
      try {
          // Step 1: Save (Create or Update)
          let projectIdToFinalize = editingProjectId;
          const projectData = assembleProjectData();

          if (editingProjectId) {
              await axiosInstance.put(`/projects/${editingProjectId}`, projectData);
          } else {
              const createResponse = await axiosInstance.post("/projects", projectData);
              projectIdToFinalize = createResponse.data.id;
              setProjects(prev => [...prev, createResponse.data]);
          }

          // Step 2: Finalize
          const finalizeResponse = await axiosInstance.post(`/projects/${projectIdToFinalize}/finalize`);

          setProjects(prev => prev.map(p => p.id === projectIdToFinalize ? finalizeResponse.data : p));

          setShowCreateModal(false);
          resetForm();
          navigate(`/project/${projectIdToFinalize}`);

      } catch (error) {
          console.error("Error finalizing project:", error);
          toast.error(humanizeError(error));
      } finally {
          setFinalizingProject(false);
      }
  };

  const handleMarkCompleted = async (projectId) => {
    try {
      const response = await axiosInstance.put(`/projects/${projectId}`, {
        status: "completed"
      });
      setProjects(projects.map(p => p.id === projectId ? response.data : p));
      toast.success("Project marked as completed.");
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error(humanizeError(error));
    }
  };



  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-emerald-50/30 via-white to-blue-50/20">
      <Header user={user} />
      <div className="flex flex-1">
      <Sidebar />
        <main className="flex-1 p-8 bg-gradient-to-b from-transparent to-emerald-50/10">
          <div className="max-w-7xl mx-auto w-full">

            {/* Dashboard Header Section */}
            <div className="mb-8 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-blue-500/5 rounded-3xl blur-3xl"></div>
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-emerald-100/50 shadow-xl">
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <h1 className="text-[28px] font-extrabold bg-gradient-to-r from-gray-900 via-emerald-700 to-gray-900 bg-clip-text text-transparent mb-3">
                      Projects
                    </h1>
                    <p className="text-gray-600 text-lg font-medium">Manage advisor transitions and client onboarding</p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                    className="btn-primary flex items-center gap-3 px-6 py-3.5 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
              >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                    <span className="font-semibold">Create Project</span>
              </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="space-y-6">
                {/* Skeleton Stats Section */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="card bg-gradient-to-br from-gray-200 to-gray-300 border-0 shadow-lg animate-pulse">
                      <div className="p-4">
                        <div className="w-8 h-8 bg-gray-400 rounded-lg mb-2"></div>
                        <div className="h-8 w-16 bg-gray-400 rounded mb-1"></div>
                        <div className="h-4 w-24 bg-gray-400 rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Skeleton Project Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="card bg-gradient-to-br from-white via-emerald-50/20 to-blue-50/20 border-emerald-200/50 shadow-lg animate-pulse">
                      <div className="flex items-start justify-between mb-5">
                        <div className="w-16 h-16 bg-gray-300 rounded-2xl"></div>
                        <div className="flex gap-2">
                          <div className="w-8 h-8 bg-gray-300 rounded-lg"></div>
                          <div className="w-16 h-6 bg-gray-300 rounded"></div>
                        </div>
                      </div>
                      <div className="h-8 w-3/4 bg-gray-300 rounded mb-4"></div>
                      <div className="h-4 w-full bg-gray-200 rounded mb-4"></div>
                      <div className="flex items-center gap-4 mb-5">
                        <div className="h-8 w-24 bg-gray-200 rounded-xl"></div>
                        <div className="h-8 w-24 bg-gray-200 rounded-xl"></div>
                      </div>
                      <div className="pt-5 border-t border-gray-200">
                        <div className="h-4 w-32 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-20 card max-w-lg mx-auto bg-gradient-to-br from-white via-emerald-50/20 to-blue-50/20 border-emerald-200/50 shadow-xl">
                <div className="w-24 h-24 bg-gradient-to-br from-emerald-100 to-blue-100 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <svg className="w-14 h-14 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">No projects yet</h3>
                <p className="text-gray-600 mb-8 text-lg font-medium">Get started by creating your first project</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn-primary px-8 py-4 text-base font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
                >
                  Create Your First Project
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    onClick={() => {
                      setLoadingProjectId(project.id);
                      navigate(`/project/${project.id}`);
                    }}
                    className={`card bg-gradient-to-br from-white via-emerald-50/20 to-blue-50/20 border-emerald-200/50 shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 group cursor-pointer ${
                      loadingProjectId === project.id ? 'opacity-75 pointer-events-none' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-5">
                      <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 via-emerald-600 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditProject(project);
                          }}
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                          title="Edit Project"
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>

                      <span className={`badge text-xs px-3 py-1.5 font-semibold ${
                        project.status === 'completed' ? 'badge-success' : 
                        project.status === 'initiated' || project.status === 'created' ? 'badge-info' : 
                        'badge-warning'
                      }`}>
                        {project.status === 'created' ? 'ongoing' : (project.status || 'ongoing')}
                      </span>
                      </div>
                    </div>
                    <h3 className="text-[22px] font-bold text-gray-900 mb-4 group-hover:text-emerald-700 transition-colors">
                      {project.title}
                    </h3>
                    {project.description && (
                      <p className="text-gray-600 mb-4 font-medium line-clamp-2">{project.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm mb-5">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-xl">
                        <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="font-semibold text-emerald-700">{project.total_households || 0}</span>
                        <span className="text-emerald-600">Households</span>
                      </div>
                      {project.advisors && project.advisors.length > 0 && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-xl">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span className="font-semibold text-blue-700">{project.advisors.length}</span>
                          <span className="text-blue-600">advisor{project.advisors.length !== 1 ? 's' : ''}</span>
                        </div>
                      )}
                    </div>
                    <div className="pt-5 border-t border-gray-200">
                      <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>Created {formatDateForDisplay(project.created_at)}</span>
                      </div>
                      {project.target_end_date && (
                        <div className="flex items-center gap-2 text-xs text-gray-500 font-medium mt-1">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Target End {formatDateForDisplay(project.target_end_date)}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-emerald-600 text-sm font-semibold group-hover:gap-3 transition-all">
                      <span>View Details</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Loading Overlay */}
      {loadingProjectId && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-3xl p-8 shadow-2xl border border-emerald-100 max-w-md w-full mx-4">
            <div className="flex flex-col items-center">
              <div className="relative w-20 h-20 mb-6">
                <div className="absolute inset-0 border-4 border-emerald-200 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-transparent border-t-emerald-600 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Loading Project</h3>
              <p className="text-gray-600 text-center font-medium">
                {projects.find(p => p.id === loadingProjectId)?.title || 'Project'}
              </p>
              <div className="mt-4 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-500 to-blue-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      {/* Create/Edit Project Modal */}
      {showCreateModal && (
        <div className="modal-overlay" style={{ zIndex: 9997, overflowY: 'auto' }} onClick={handleCloseAttempt}>
          <div className="modal-content bg-gradient-to-br from-white via-emerald-50/10 to-blue-50/10 max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6 pb-5 border-b border-emerald-200/50 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h2 className="text-3xl font-extrabold bg-gradient-to-r from-gray-900 via-emerald-700 to-blue-700 bg-clip-text text-transparent">
                  {editingProjectId ? "Edit Project" : "Create New Project"}
                </h2>
              </div>
              <button
                onClick={handleCloseAttempt}
                className="btn-ghost p-2 rounded-lg hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Steps Indicator */}
            <div className="mb-8 px-4">
                <div className="flex items-center justify-between relative">
                    <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-1 bg-gray-200 -z-10"></div>
                    <div className={`flex flex-col items-center gap-2 bg-white px-4 ${step >= 1 ? 'text-emerald-600' : 'text-gray-400'}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg border-2 ${step >= 1 ? 'bg-emerald-100 border-emerald-500' : 'bg-gray-100 border-gray-300'}`}>1</div>
                        <span className="font-semibold text-sm">Details</span>
                    </div>
                    <div className={`flex flex-col items-center gap-2 bg-white px-4 ${step >= 2 ? 'text-emerald-600' : 'text-gray-400'}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg border-2 ${step >= 2 ? 'bg-emerald-100 border-emerald-500' : 'bg-gray-100 border-gray-300'}`}>2</div>
                        <span className="font-semibold text-sm">Advisors & Defaults</span>
                    </div>
                    <div className={`flex flex-col items-center gap-2 bg-white px-4 ${step >= 3 ? 'text-emerald-600' : 'text-gray-400'}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg border-2 ${step >= 3 ? 'bg-emerald-100 border-emerald-500' : 'bg-gray-100 border-gray-300'}`}>3</div>
                        <span className="font-semibold text-sm">Review & Finalize</span>
                    </div>
                </div>
            </div>

            <form onSubmit={(e) => e.preventDefault()} className="space-y-8">
              
              {/* Step 1: Project Details */}
              {step === 1 && (
                  <div className="space-y-6">
                       <div>
                          <label className="block text-sm font-bold text-gray-700 mb-3">Project Title *</label>
                          <input
                            type="text"
                            value={projectTitle}
                            onChange={(e) => setProjectTitle(e.target.value)}
                            className="input-modern text-base w-full"
                            placeholder="Enter project title"
                            autoFocus
                          />
                      </div>
                      
                      {lockCustodianConfig && (
                          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                              Custodian and firm are set when the project is created and can't be changed afterwards.
                          </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                              <label className="block text-sm font-bold text-gray-700 mb-3">Source Custodian</label>
                               <select
                                  value={sourceCustodian}
                                  onChange={(e) => setSourceCustodian(e.target.value)}
                                  disabled={lockCustodianConfig}
                                  className={`input-modern text-base w-full ${lockCustodianConfig ? "opacity-60 cursor-not-allowed bg-gray-100" : ""}`}
                               >
                                  <option value="">Select...</option>
                                  {SOURCE_CUSTODIAN_OPTIONS.map((firm) => (
                                    <option key={firm} value={firm}>
                                      {firm}
                                    </option>
                                  ))}
                               </select>
                          </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                              <label className="block text-sm font-bold text-gray-700 mb-3">Destination Custodian *</label>
                               <select
                                  value={custodian}
                                  onChange={(e) => setCustodian(e.target.value)}
                                  disabled={lockCustodianConfig}
                                  className={`input-modern text-base w-full ${lockCustodianConfig ? "opacity-60 cursor-not-allowed bg-gray-100" : ""}`}
                               >
                                  {CUSTODIAN_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                               </select>
                          </div>
                          <div>
                              <label className="block text-sm font-bold text-gray-700 mb-3">Destination Firm *</label>
                               <select
                                  value={destinationFirm}
                                  onChange={(e) => setDestinationFirm(e.target.value)}
                                  disabled={lockCustodianConfig}
                                  className={`input-modern text-base w-full ${lockCustodianConfig ? "opacity-60 cursor-not-allowed bg-gray-100" : ""}`}
                               >
                                  <option value="">Select Firm...</option>
                                  {DESTINATION_FIRM_OPTIONS.map((firm) => (
                                    <option key={firm} value={firm}>
                                      {firm}
                                    </option>
                                  ))}
                               </select>
                          </div>
                      </div>

                      {/* Custodian Types (Only for Fidelity) */}
                      {custodian === "Fidelity" && (
                          <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
                              <label className="block text-sm font-bold text-gray-700 mb-3">Custodian Types</label>
                              <div className="flex gap-6">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                      <input 
                                          type="checkbox" 
                                          checked={custodianTypes.iws} 
                                          onChange={(e) => setCustodianTypes({...custodianTypes, iws: e.target.checked})}
                                          className="text-emerald-600 w-5 h-5 rounded hover:scale-110 transition-transform"
                                      />
                                      <span className="font-semibold text-gray-700">IWS</span>
                                  </label>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                      <input 
                                          type="checkbox" 
                                          checked={custodianTypes.nfs} 
                                          onChange={(e) => setCustodianTypes({...custodianTypes, nfs: e.target.checked})}
                                          className="text-emerald-600 w-5 h-5 rounded hover:scale-110 transition-transform"
                                      />
                                      <span className="font-semibold text-gray-700">NFS</span>
                                  </label>
                              </div>
                          </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                              <label className="block text-sm font-bold text-gray-700 mb-3">Do you have AUM values for your client accounts?</label>
                              <div className="space-y-3">
                                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all">
                                      <input
                                        type="radio"
                                        name="aumAvailability"
                                        checked={aumAvailability === 1}
                                        onChange={() => setAumAvailability(1)}
                                        className="text-emerald-600 w-5 h-5"
                                      />
                                      <span className="text-sm font-medium text-gray-700">Yes — I'll enter values during household setup</span>
                                  </label>
                                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all">
                                      <input
                                        type="radio"
                                        name="aumAvailability"
                                        checked={aumAvailability === 2}
                                        onChange={() => setAumAvailability(2)}
                                        className="text-emerald-600 w-5 h-5"
                                      />
                                      <span className="text-sm font-medium text-gray-700">I'll add it via document import</span>
                                  </label>
                                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all">
                                      <input
                                        type="radio"
                                        name="aumAvailability"
                                        checked={aumAvailability === 3}
                                        onChange={() => setAumAvailability(3)}
                                        className="text-emerald-600 w-5 h-5"
                                      />
                                      <span className="text-sm font-medium text-gray-700">No — Skip AUM tracking for this project</span>
                                  </label>
                              </div>
                          </div>
                          <div>
                              <label className="block text-sm font-bold text-gray-700 mb-3">Target End Date *</label>
                              <div
                                className="relative w-full"
                                onClick={() => { if (targetEndDateRef.current?.showPicker) targetEndDateRef.current.showPicker(); else targetEndDateRef.current?.focus(); }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); targetEndDateRef.current?.showPicker?.(); } }}
                              >
                                <input
                                  type="text"
                                  readOnly
                                  value={targetEndDate ? formatDateForDisplay(targetEndDate) : ""}
                                  placeholder="mm-dd-yyyy"
                                  className="input-modern text-base w-full cursor-pointer"
                                  aria-readonly
                                />
                                <input
                                  ref={targetEndDateRef}
                                  type="date"
                                  value={targetEndDate}
                                  onChange={(e) => setTargetEndDate(e.target.value)}
                                  min={(() => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0'); })()}
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                  tabIndex={-1}
                                  aria-hidden
                                />
                              </div>
                          </div>
                      </div>
                  </div>
              )}

              {/* Step 2: Advisors & Defaults */}
              {step === 2 && (
                  <div className="space-y-8">
                       {/* Advisors Section */}
                       <div>
                          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                              <span className="bg-emerald-100 text-emerald-800 p-1 rounded">Advisors</span>
                              <span className="text-sm font-normal text-gray-500">(Required)</span>
                          </h3>
                          <div className="space-y-4">
                              {advisors.map((advisor, index) => (
                                <div key={index} className="p-5 border border-gray-200 rounded-xl bg-gray-50/50 hover:bg-white hover:shadow-md transition-all duration-200">
                                  <div className="flex items-center justify-between mb-4">
                                    <h4 className="font-bold text-gray-700">Advisor {index + 1}</h4>
                                    {advisors.length > 1 && (
                                      <button type="button" onClick={() => handleRemoveAdvisor(index)} className="text-red-500 hover:text-red-700 text-sm font-semibold transition-colors">REMOVE</button>
                                    )}
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div>
                                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">First Name *</label>
                                      <input type="text" value={advisor.first_name} onChange={(e) => handleAdvisorChange(index, 'first_name', e.target.value)} required className="input-modern text-sm w-full bg-white" placeholder="First Name" />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Last Name *</label>
                                      <input type="text" value={advisor.last_name} onChange={(e) => handleAdvisorChange(index, 'last_name', e.target.value)} required className="input-modern text-sm w-full bg-white" placeholder="Last Name" />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email *</label>
                                      <input type="email" value={advisor.email || ""} onChange={(e) => handleAdvisorChange(index, 'email', e.target.value)} required className="input-modern text-sm w-full bg-white" placeholder="advisor@example.com" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">G Number</label>
                                            <input type="text" value={advisor.g_number} onChange={(e) => handleAdvisorChange(index, 'g_number', e.target.value)} className="input-modern text-sm w-full bg-white" placeholder="Optional" />
                                        </div>
                                        {custodianTypes.nfs && (
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">RR Code (NFS)</label>
                                                <input type="text" value={advisor.rr_code} onChange={(e) => handleAdvisorChange(index, 'rr_code', e.target.value)} className="input-modern text-sm w-full bg-white" placeholder="Optional" />
                                            </div>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                          <button type="button" onClick={handleAddAdvisor} className="mt-4 btn-secondary text-sm px-4 py-2 flex items-center gap-2 font-semibold">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
                              Add Another Advisor
                          </button>
                       </div>


                       {/* IWS Defaults */}
                       {custodianTypes.iws && (
                           <div className="border-t border-emerald-100 pt-6">
                               <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                  <span className="bg-blue-100 text-blue-800 p-1 rounded">IWS Default Details</span>
                               </h3>
                               
                               <div className="space-y-6 mb-8">
                                   <div>
                                       <label className="block text-xs font-semibold text-gray-600 mb-1">Additional Authorised Owner/Firm Name</label>
                                       <div className="grid grid-cols-2 gap-4">
                                           <input type="text" placeholder="Firm Name" value={iwsAdditionalAuthorisedOwner.single_name} onChange={(e) => setIwsAdditionalAuthorisedOwner({...iwsAdditionalAuthorisedOwner, single_name: e.target.value})} className="input-modern text-sm w-full" />
                                           <input type="text" placeholder="G Number" value={iwsAdditionalAuthorisedOwner.g_number} onChange={(e) => setIwsAdditionalAuthorisedOwner({...iwsAdditionalAuthorisedOwner, g_number: e.target.value})} className="input-modern text-sm w-full" />
                                       </div>
                                   </div>
                                   <div>
                                       <label className="block text-xs font-semibold text-gray-600 mb-1">Advisor Firm Name/BD/OSJ Details</label>
                                       <div className="grid grid-cols-2 gap-4">
                                           <input type="text" placeholder="Full name" value={iwsAdvisorFirmName.single_name} onChange={(e) => setIwsAdvisorFirmName({...iwsAdvisorFirmName, single_name: e.target.value})} className="input-modern text-sm w-full" />
                                           <input type="text" placeholder="G Number" value={iwsAdvisorFirmName.g_number} onChange={(e) => setIwsAdvisorFirmName({...iwsAdvisorFirmName, g_number: e.target.value})} className="input-modern text-sm w-full" />
                                       </div>
                                   </div>
                               </div>

                               <h3 className="text-lg font-bold text-gray-800 mb-1">IWS Preferences</h3>
                               <p className="text-xs text-gray-500 mb-6">Please note, these are set by default but you can change these and it will reflect across this transition project</p>
                               
                               <div className="space-y-4">
                                 {[
                                     { key: 'proxies', label: 'Proxies:', state: iwsProxies, setter: setIwsProxies },
                                     { key: 'reports', label: 'Reports:', state: iwsReports, setter: setIwsReports },
                                     { key: 'prospectuses', label: 'Prospectuses:', state: iwsProspectuses, setter: setIwsProspectuses },
                                     { key: 'corporate_action', label: 'Corporate Action:', state: iwsCorporateAction, setter: setIwsCorporateAction }
                                 ].map(({ key, label, state, setter }) => (
                                     <div key={key} className="flex items-center gap-4">
                                         <label className="text-sm font-bold text-gray-800 w-40">{label}</label>
                                         <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" checked={state.send_to_advisor} onChange={() => setter({...state, send_to_advisor: true, send_to_client: false})} className="text-purple-600 w-4 h-4" /> Send to Advisor</label>
                                         <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" checked={state.send_to_client} onChange={() => setter({...state, send_to_advisor: false, send_to_client: true})} className="text-purple-600 w-4 h-4" /> Send to Client</label>
                                     </div>
                                 ))}

                                 <div className="flex items-center gap-4 pt-2">
                                     <label className="flex items-center gap-2 text-sm font-bold text-gray-800 cursor-pointer">
                                         <input
                                             type="checkbox"
                                             checked={iwsProxyGNumberEnabled}
                                             onChange={(e) => {
                                                 const enabled = e.target.checked;
                                                 setIwsProxyGNumberEnabled(enabled);
                                                 if (!enabled) setIwsProxies({ ...iwsProxies, g_number: null });
                                             }}
                                             className="text-purple-600 w-5 h-5 rounded"
                                         />
                                         Add Proxy Voting G-Number
                                     </label>
                                     {iwsProxyGNumberEnabled && (
                                         <input
                                             type="text"
                                             placeholder="Proxy Voting G-Number"
                                             value={iwsProxies.g_number || ""}
                                             onChange={(e) => setIwsProxies({ ...iwsProxies, g_number: e.target.value })}
                                             className="input-modern text-sm w-56"
                                         />
                                     )}
                                 </div>

                                 <div className="flex items-center gap-4 pt-2">
                                     <label className="text-sm font-bold text-gray-800 w-40">Outsourcing Agent Authorisation:</label>
                                     <input type="checkbox" checked={iwsOutsourcingAgentAuthorisation} onChange={(e) => setIwsOutsourcingAgentAuthorisation(e.target.checked)} className="text-purple-600 w-5 h-5 rounded" />
                                 </div>
                               </div>
                           </div>
                       )}
                       {/* NFS Defaults */}
                       {custodianTypes.nfs && (
                            <div className="border-t border-emerald-100 pt-6">
                                <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
                                   <span className="bg-purple-100 text-purple-800 p-1 rounded">NFS Default Details</span>
                                </h3>
                               <p className="text-xs text-gray-500 mb-6">Please note that default values will override advisor details for all NFS forms</p>
                               
                               <div className="mb-4">
                                  <label className="block text-xs font-semibold text-gray-600 mb-1">Branch Prefix</label>
                                  <input type="text" value={nfsBranchPrefix} onChange={(e) => setNfsBranchPrefix(e.target.value)} className="input-modern text-sm w-1/2 bg-white" placeholder="Ex: ABC" />
                               </div>

                               <div className="space-y-4 mb-8">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-2">Are Holders employees of your B/D</label>
                                        <div className="flex gap-4">
                                            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" name="holdersEmployees" checked={nfsAreHoldersEmployees === "No"} onChange={() => setNfsAreHoldersEmployees("No")} className="text-emerald-600" /> No</label>
                                            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" name="holdersEmployees" checked={nfsAreHoldersEmployees === "Yes"} onChange={() => setNfsAreHoldersEmployees("Yes")} className="text-emerald-600" /> Yes</label>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-2">Is this a Managed account</label>
                                        <div className="flex gap-4">
                                            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" name="managedAccount" checked={nfsIsManagedAccount === "No"} onChange={() => setNfsIsManagedAccount("No")} className="text-emerald-600" /> No</label>
                                            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" name="managedAccount" checked={nfsIsManagedAccount === "Yes"} onChange={() => setNfsIsManagedAccount("Yes")} className="text-emerald-600" /> Yes</label>
                                        </div>
                                    </div>
                               </div>

                               <h3 className="text-lg font-bold text-gray-800 mb-1">NFS Preferences</h3>
                               <p className="text-xs text-gray-500 mb-6">Please note, these are set by default but you can change these and it will reflect across forms of this transition project</p>
                               
                               <div className="space-y-4">
                                   <div className="space-y-2">
                                      <p className="text-xs font-bold text-gray-500 uppercase">Suitability - Decision Making Experience</p>
                                      <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={nfsConsultWithBroker.yes} onChange={(e) => setNfsConsultWithBroker({yes: e.target.checked, no: !e.target.checked})} className="rounded text-emerald-600" /> I consult with my broker</label>
                                      <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={nfsMakeOwnDecision.yes} onChange={(e) => setNfsMakeOwnDecision({yes: e.target.checked, no: !e.target.checked})} className="rounded text-emerald-600" /> I make my own decision</label>
                                      <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={nfsConsultWithFamilyFriends.yes} onChange={(e) => setNfsConsultWithFamilyFriends({yes: e.target.checked, no: !e.target.checked})} className="rounded text-emerald-600" /> I consult with my family/friends</label>
                                   </div>
                                   <label className="flex items-center gap-2 cursor-pointer mt-4">
                                      <input type="checkbox" checked={nfsEdelivery} onChange={(e) => setNfsEdelivery(e.target.checked)} className="text-emerald-600 w-5 h-5 rounded" />
                                      <span className="text-sm font-semibold">E-delivery</span>
                                   </label>
                               </div>
                            </div>
                       )}
                       
                       {/* Fee Defaults */}
                       {step === 2 && (
                           <div className="border-t border-emerald-100 pt-6">
                              <h3 className="text-lg font-bold text-gray-800 mb-4">Fee Defaults</h3>
                              <div className="flex gap-6">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                      <input 
                                          type="radio" 
                                          name="feeType"
                                          checked={feeScheduleType === "Standard"}
                                          onChange={() => setFeeScheduleType("Standard")}
                                          className="text-emerald-600 w-5 h-5"
                                      />
                                      <span className="text-sm font-medium">Standard Fee Schedule</span>
                                  </label>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                      <input 
                                          type="radio" 
                                          name="feeType"
                                          checked={feeScheduleType === "Custom"}
                                          onChange={() => setFeeScheduleType("Custom")}
                                          className="text-emerald-600 w-5 h-5"
                                      />
                                      <span className="text-sm font-medium">Custom Fee Schedule</span>
                                  </label>
                              </div>
                           
                           {/* Custom Fee Percentage Input */}
                           {feeScheduleType === "Custom" && (
                               <div className="mt-4 animate-fadeIn">
                                   <label className="block text-sm font-bold text-gray-700 mb-2">Custom Fee Percentage *</label>
                                   <div className="relative max-w-[200px]">
                                       <input
                                           type="number"
                                           step="0.01"
                                           min="0"
                                           max="100"
                                           value={customFeePercentage}
                                           onChange={(e) => setCustomFeePercentage(e.target.value)}
                                           className="input-modern text-base w-full pr-8"
                                           placeholder="0.00"
                                           required
                                       />
                                       <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-bold">%</span>
                                   </div>
                               </div>
                           )}
                           </div>
                       )}
                   </div>
               )}

                        {step === 3 && (
                            <div className="space-y-6 animate-fadeIn">
                                <div className="bg-emerald-50/50 p-6 rounded-xl border border-emerald-100">
                                    <h3 className="text-lg font-bold text-gray-800 mb-4">Review Project Details</h3>
                                    <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm">
                                        <div>
                                            <span className="text-gray-500 block mb-1">Project Title</span>
                                            <span className="font-semibold text-gray-900">{projectTitle}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-1">Destination Custodian</span>
                                            <span className="font-semibold text-gray-900">{custodian}</span>
                                            {custodian === "Fidelity" && (custodianTypes.iws || custodianTypes.nfs) && (
                                                <div className="flex gap-2 mt-1.5">
                                                    {custodianTypes.iws && <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs">IWS</span>}
                                                    {custodianTypes.nfs && <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs">NFS</span>}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-1">Total Advisors</span>
                                            <span className="font-semibold text-gray-900">{advisors.length}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-1">Fee Schedule</span>
                                            <span className="font-semibold text-gray-900">
                                                {feeScheduleType} Fee Schedule
                                                {feeScheduleType === "Custom" && customFeePercentage && ` (${customFeePercentage}%)`}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-1">Target End Date</span>
                                            <span className="font-semibold text-gray-900">
                                                {targetEndDate ? formatDateForDisplay(targetEndDate) : 'Not set'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-1">AUM Availability</span>
                                            <span className="font-semibold text-gray-900">{AUM_AVAILABILITY_OPTIONS[aumAvailability]}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-blue-50/50 px-3 py-2 rounded-xl border border-blue-100 flex items-start gap-2">
                                    <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    <p className="text-sm text-blue-800">Please review all details before creating the project. You can edit these details later from the project dashboard.</p>
                                </div>
                            </div>
                        )}

                       {/* Footer */}
                       <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">
                           <button 
                               onClick={handlePrevStep}
                               disabled={step === 1}
                               className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${step === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}
                           >
                               Back
                           </button>
                           
                           <div className="flex gap-3">
                               {!editingProjectId && (
                                   <button 
                                       onClick={(e) => handleCreateProject(e)}
                                       disabled={finalizingProject}
                                       className="px-4 py-2 rounded-lg text-sm font-semibold text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                   >
                                       Save as Draft
                                   </button>
                               )}
                               
                               {step < 3 ? (
                                   <button 
                                        onClick={handleNextStep}
                                       className="px-6 py-2 bg-gradient-to-r from-emerald-600 to-blue-600 text-white rounded-lg text-sm font-semibold shadow-md hover:shadow-lg hover:from-emerald-700 hover:to-blue-700 transition-all"
                                   >
                                       Next Step
                                   </button>
                               ) : (
                                   <button 
                                       onClick={handleFinalizeProject}
                                       disabled={finalizingProject}
                                       className="px-6 py-2 bg-gradient-to-r from-emerald-600 to-blue-600 text-white rounded-lg text-sm font-semibold shadow-md hover:shadow-lg hover:from-emerald-700 hover:to-blue-700 transition-all flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                                   >
                                       {finalizingProject ? (
                                           <>
                                               <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                               Finalizing...
                                           </>
                                       ) : (
                                           <>
                                               Finalize Project
                                               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                           </>
                                       )}
                                   </button>
                               )}
                           </div>
                       </div>
                    </form>
                </div>
            </div>
            )}
            {/* Exit Prompt Modal */}
            {showExitPrompt && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100 animate-fadeIn" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">Unsaved Changes</h3>
                                    <p className="text-gray-500 text-sm mt-1">Do you really want to close the project setup? Unsaved progress will be lost.</p>
                                </div>
                            </div>
                            
                            <div className="flex flex-col gap-3 mt-6">
                                <button 
                                    onClick={() => handleCreateProject(null)}
                                    className="w-full py-3 bg-gradient-to-r from-emerald-600 to-blue-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                    </svg>
                                    Save as Draft
                                </button>
                                
                                <button 
                                    onClick={handleExitWithoutSaving}
                                    className="w-full py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-300 transition-colors"
                                >
                                    Exit without Saving
                                </button>
                                
                                <button 
                                    onClick={() => setShowExitPrompt(false)}
                                    className="w-full py-2 text-gray-500 hover:text-gray-700 font-medium text-sm transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
